/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import { useEffect, useState } from 'react';

import { postLead } from '@core/Utils/Leads/PostLead';
import { Button } from '@gds/Button/Button';
import { Checkbox } from '@gds/Checkbox/Checkbox';
import { Dropdown, DropdownOptionProps } from '@gds/Dropdown/Dropdown';
import Grid from '@gds/Grid/Grid';

import { Germany } from '@gds/Icons';
import { Input } from '@gds/Input/Input';
import { Typography } from '@gds/Typography/Typography';

import { countries, months } from './InputForm.constants';

import { InputFormData, InputFormProps, ManufacturerItem } from './InputForm.entity';

import styles from './InputForm.module.css';

const API_BASE_URL = process.env.NEXT_PUBLIC_CARSALE_API_URL;

const InputForm = ({ inputFormTitle }: InputFormProps) => {
  const CARSALE_SUBMIT_URL = `${API_BASE_URL}/de-de/seller/registration/heycar`;
  const [manufacturers, setManufacturers] = useState<DropdownOptionProps[]>([]);
  const [models, setModels] = useState<DropdownOptionProps[]>([]);
  const [formData, setFormData] = useState<InputFormData>({
    month: { label: '', value: '' },
    year: '',
    make: { label: '', value: '' },
    model: { label: '', value: '' },
    mileage: 0,
    countryId: { label: 'Deutschland', value: '1', leftContent: <Germany /> },
    zipCode: '',
    email: '',
    telephone: '',
    acceptedTerms: false,
  });

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1949 }, (_, i) => String(currentYear - i));

  useEffect(() => {
    const fetchManufacturers = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch('/api/mam/brands');
        const data = await response.json();

        const manufacturerList: ManufacturerItem[] = Object.entries(data)
          .filter(([_, name]) => name !== 'Bitte auswählen')
          .map(([id, name]) => ({
            id,
            name: name as string,
          }));

        setManufacturers(
          manufacturerList.map(item => ({
            label: item.name,
            value: item.id,
          })),
        );
      } catch (error: unknown) {
        console.error('Error fetching manufacturers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchManufacturers();
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      const { month, year, make } = formData;

      if (!make || !month || !year) return;

      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `/api/mam/models?initialRegistrationMonth=${month.value}&initialRegistrationYear=${year}&datManufacturer=${make.value}`,
        );
        const data = await response.json();
        const modelList: ManufacturerItem[] = Object.entries(data.models)
          .filter(([id, name]) => id !== '' && name !== 'Bitte auswählen')
          .map(([id, name]) => ({
            id,
            name: name as string,
          }));

        setModels(
          modelList.map(item => ({
            label: item.name,
            value: item.id,
          })),
        );
      } catch (error: unknown) {
        {
          formData.month.value &&
            formData.year &&
            formData.make.value &&
            setError(error instanceof Error ? error.message : 'An error occurred');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, [formData.month, formData.year, formData.make]);

  const handleFormSubmit = async () => {
    await postLead('de', {
      lead_type: 'part_exchange',
      subtype: 'trade_in_lead',
      status: 'active',
      source: 'heycar',
      is_test: false,
      final: true,
      attribution: null,
      items: [],
      customer: {
        first_name: '',
        last_name: '',
        email: formData.email,
        phone: formData.telephone,
        address: { postcode: '13347' },
        optin_preferences: {
          optin_source: 'message_submit',
        },
      },
      arbitrary_data: {
        message: '',
        referring_page: 'de/value-my-car',
        make: formData.make.label,
        model: formData.model.label,
        registration_date: `${formData.month.value}.${formData.year}`,
        mileage: formData.mileage.toString(),
      },
    });
  };

  return (
    <form
      action={CARSALE_SUBMIT_URL}
      method="POST"
      target="_blank"
      onSubmit={handleFormSubmit}
      id="carsale-form"
    >
      <Typography variant="h4" className={styles.heading}>
        {error
          ? 'Bei diesem Formular ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
          : inputFormTitle}
      </Typography>

      <Grid.Container>
        <Grid.Row>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.field}>
            <input type="hidden" name="initialRegistrationMonth" value={formData.month.value} />
            <Dropdown
              label="Monat"
              value={formData.month}
              onChange={option =>
                setFormData(prev => ({ ...prev, month: option as DropdownOptionProps }))
              }
              required
              options={months}
            />
          </Grid.Col>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.field}>
            <Dropdown
              name="initialRegistrationYear"
              label="Jahr"
              value={{ label: formData.year, value: formData.year }}
              onChange={option =>
                setFormData(prev => ({ ...prev, year: (option?.value as string) || '' }))
              }
              required
              options={years.map(year => ({ label: year, value: year }))}
            />
          </Grid.Col>
        </Grid.Row>

        <Grid.Row>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.field}>
            <input type="hidden" name="datManufacturer" value={formData.make.value} />
            <Dropdown
              label="Marke"
              value={manufacturers.find(m => m.value === formData.make.value)}
              onChange={option =>
                setFormData(prev => ({
                  ...prev,
                  make: (option as DropdownOptionProps) || '',
                  model: { value: '', label: '' },
                }))
              }
              required
              options={manufacturers}
            />
          </Grid.Col>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.field}>
            <input type="hidden" name="datMainType" value={formData.model.value} />
            <Dropdown
              label="Modell"
              value={models.find(m => m.value === formData.model.value)}
              onChange={option =>
                setFormData(prev => ({ ...prev, model: (option as DropdownOptionProps) || '' }))
              }
              disabled={
                !formData.make.value || !formData.month.value || !formData.year || isLoading
              }
              required
              options={models}
            />
          </Grid.Col>
        </Grid.Row>

        <Grid.Row>
          <Grid.Col width={{ mobile: 12, tablet: 4.25 }} className={styles.field}>
            <Input
              name="kilometer"
              type="number"
              label="Kilometerstand"
              value={formData.mileage || ''}
              onChange={e =>
                setFormData(prev => ({ ...prev, mileage: parseInt(e.target.value) || 0 }))
              }
              required
              fullWidth
            />
          </Grid.Col>
          <Grid.Col width={{ mobile: 12, tablet: 3.5 }} className={styles.field}>
            <input type="hidden" name="countryId" value={formData.countryId.value} />
            <Dropdown
              label="Land"
              value={formData.countryId}
              onChange={option =>
                setFormData(prev => ({ ...prev, countryId: option as DropdownOptionProps }))
              }
              required
              options={countries}
              showLeftLabelIcon
            />
          </Grid.Col>
          <Grid.Col width={{ mobile: 12, tablet: 4.25 }} className={styles.field}>
            <Input
              name="datCarZipcode"
              type="text"
              label="Fzg. Standort (PLZ)"
              value={formData.zipCode}
              onChange={e => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
              required
              fullWidth
            />
          </Grid.Col>
        </Grid.Row>

        <Grid.Row>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.field}>
            <Input
              name="email"
              type="email"
              label="E-Mail-Adresse"
              value={formData.email}
              onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
            />
          </Grid.Col>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.field}>
            <Input
              name="phone"
              type="tel"
              label="Telefonnummer (optional)"
              value={formData.telephone}
              onChange={e => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
              fullWidth
            />
          </Grid.Col>
        </Grid.Row>

        <Grid.Row className={styles.checkboxRow}>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.checkbox}>
            <Checkbox
              name="privacy"
              value="1"
              checked={formData.acceptedTerms}
              onChange={e => setFormData(prev => ({ ...prev, acceptedTerms: e.target.checked }))}
              required
            />
            <Typography variant="subheading4">
              Ich akzeptiere die{' '}
              <a
                href="https://www.heycar.com/de/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Datenschutzerklärung
              </a>
            </Typography>
          </Grid.Col>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.submitCol}>
            <Button
              type="submit"
              disabled={
                !formData.acceptedTerms ||
                !formData.month ||
                !formData.year ||
                !formData.make ||
                !formData.model ||
                !formData.mileage ||
                !formData.countryId ||
                !formData.zipCode ||
                !formData.email
              }
            >
              Auto verkaufen
            </Button>
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col width={{ mobile: 12, desktop: 6 }} className={styles.checkbox}>
            <Typography variant="caption1">
              Weiterleitung zu carsale24.{' '}
              <a
                href="https://app.carsale24.com/de-de/seller/registration/contact-data?no-redirect=1&a_aid=heycar&customData=estimate-main_nav"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Ihr Fahrzeug nicht dabei?
              </a>
            </Typography>
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>
    </form>
  );
};

export default InputForm;
